/**
 * The message a Search export sends ("Attach to a study" → Message step): the sender writes it,
 * or starts from a saved template and edits it. Pure helpers — the checks, the variables it may
 * use, and the preview render — shared by ExportModal and its tests.
 *
 * Mirrors the server (backend services/crmSearch/exportCampaign.js normalizeCampaignMessage +
 * services/crmTemplate.js renderTemplate). The server re-checks at launch, so an `errors` entry
 * here is a message the server would refuse, worded the way exportParamErrorText words the
 * refusal; `warnings` never block.
 */
import { findUnknownMergeFields } from '../../mergeFields';
import { BRAND_NAME, smsSegments } from '../../outreach/outreachLogic';
import type { VariableDef } from '../VariablePicker';

export type CampaignChannel = 'sms' | 'email';

/** = exportCampaign MESSAGE_LIMITS (and the send path's own ceilings). */
export const CAMPAIGN_LIMITS = { sms: 1600, email_body: 10000, email_subject: 200 } as const;

/**
 * What a list message can fill in. `{{answer.<id>}}` is left out on purpose: the scheduled-send
 * path renders without the lead's answers, so it would go out blank (the server refuses it).
 */
export const CAMPAIGN_VARIABLES: VariableDef[] = [
  { token: 'first_name', label: 'First name', description: "The person's first name." },
  { token: 'last_name', label: 'Last name', description: "The person's last name." },
  { token: 'full_name', label: 'Full name', description: 'First and last name together.' },
  {
    token: 'study_title',
    label: 'Study name',
    description: "The public title of the study they're on when it sends — this one, if you move them to it.",
  },
];

/** One person the preflight lets us preview as (exportCampaign.buildCampaignPreview). */
export interface CampaignPreviewPerson {
  first_name: string;
  last_name: string;
  full_name: string;
  study_title: string;
  study_slug: string;
}

export interface CampaignMessageCheck {
  /** Would be refused at launch — Launch stays disabled while any are present. */
  errors: string[];
  /** Worth a second look; never blocking. */
  warnings: string[];
  chars: number;
  /** Texts only: how many SMS segments the raw message is (names change it a little). */
  segments: number;
  usesFirstName: boolean;
  usesStudy: boolean;
}

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

function tokensOf(text: string): string[] {
  return Array.from((text || '').matchAll(TOKEN_RE)).map((m) => m[1]);
}

const braces = (ts: string[]) => ts.map((t) => `{{${t}}}`).join(', ');

export function checkCampaignMessage({ channel, body, subject, studyTitle }: {
  channel: CampaignChannel;
  body: string;
  subject?: string;
  /** The public title of the chosen study, to catch it typed out by hand. */
  studyTitle?: string | null;
}): CampaignMessageCheck {
  const text = (body || '').trim();
  const subj = channel === 'email' ? (subject || '').trim() : '';
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!text) errors.push('Write the message first.');
  const bodyMax = channel === 'email' ? CAMPAIGN_LIMITS.email_body : CAMPAIGN_LIMITS.sms;
  if (text.length > bodyMax) errors.push(`The message is too long — keep it under ${bodyMax.toLocaleString()} characters.`);
  if (channel === 'email') {
    if (!subj) errors.push('Add a subject line for the email.');
    if (subj.length > CAMPAIGN_LIMITS.email_subject) {
      errors.push(`The subject line is too long — keep it under ${CAMPAIGN_LIMITS.email_subject} characters.`);
    }
  }
  const unknown = Array.from(new Set([...findUnknownMergeFields(text), ...findUnknownMergeFields(subj)]));
  if (unknown.length) errors.push(`The message uses something we can't fill in for each person: ${braces(unknown)}. Use the Variables list.`);
  const answers = Array.from(new Set([...tokensOf(text), ...tokensOf(subj)].filter((t) => t.startsWith('answer.'))));
  if (answers.length) errors.push(`Form answers can't be filled into a message sent to a list: ${braces(answers)}.`);

  const tokens = [...tokensOf(text), ...tokensOf(subj)];
  const usesStudy = tokens.includes('study_title') || tokens.includes('study_slug');
  const title = (studyTitle || '').trim();
  const namesStudy = usesStudy || (title.length > 3 && `${subj}\n${text}`.toLowerCase().includes(title.toLowerCase()));
  const segments = channel === 'sms' ? smsSegments(text) : 0;

  if (text && channel === 'sms') {
    if (!text.toLowerCase().includes(BRAND_NAME.toLowerCase())) {
      warnings.push(`Say who it's from — people ignore texts from numbers they don't know. Include "${BRAND_NAME}".`);
    }
    if (!/\bstop\b/i.test(text)) warnings.push('Tell people how to opt out, like "Reply STOP to opt out."');
    if (segments > 3) warnings.push(`This is ${segments} texts long. Shorter messages get more replies.`);
  }
  if (text && namesStudy) {
    warnings.push('This names the study, which can reveal a health condition to whoever sees the message.');
  }

  return {
    errors,
    warnings,
    chars: text.length,
    segments,
    usesFirstName: tokens.includes('first_name'),
    usesStudy,
  };
}

/**
 * Fill the message in for one person, the way the send path does. Unknown tokens stay visible
 * (so they get fixed); the server would refuse them anyway.
 */
export function renderCampaignMessage(text: string, person: CampaignPreviewPerson): string {
  const values: Record<string, string> = {
    first_name: person.first_name || '',
    last_name: person.last_name || '',
    full_name: person.full_name || [person.first_name, person.last_name].filter(Boolean).join(' '),
    study_title: person.study_title || '',
    study_slug: person.study_slug || '',
  };
  return (text || '').replace(TOKEN_RE, (m, k: string) => (k in values ? values[k] : m));
}

/** "Ana Diaz" · "Lee" · "Someone with no name on file". */
export function previewPersonName(p: CampaignPreviewPerson): string {
  return (p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ')).trim() || 'Someone with no name on file';
}
